import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UploadSimple, Users, Plugs, FileArrowUp, LinkSimple, ArrowsClockwise, Graph, Envelope, Trash, Eye, Broom, Archive, DownloadSimple } from "@phosphor-icons/react";

export default function Administration() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [mining, setMining] = useState(null); // file_id being mined
  const [sn, setSn] = useState(null); // {config, recent_incidents, summary}
  const [snSyncing, setSnSyncing] = useState(false);
  const [digest, setDigest] = useState(null);
  const [newRecipient, setNewRecipient] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [senderDraft, setSenderDraft] = useState("");
  const [showDomainHelp, setShowDomainHelp] = useState(false);
  const [reaper, setReaper] = useState(null);
  const [reaping, setReaping] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [includeFiles, setIncludeFiles] = useState(true);

  const load = async () => {
    try {
      const [f, u, s, d] = await Promise.all([
        api.get("/files"),
        user?.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] }),
        api.get("/connectors/servicenow"),
        api.get("/digest/settings"),
      ]);
      setFiles(f.data);
      setUsers(u.data);
      setSn(s.data);
      setDigest(d.data);
      setSenderDraft(d.data?.sender_override || "");
      if (user?.role === "admin") {
        try {
          const r = await api.get("/admin/reaper/status");
          setReaper(r.data);
        } catch (_e) {}
        try {
          const e = await api.get("/export/status");
          setExportStatus(e.data);
        } catch (_e) {}
      }
    } catch (_e) {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = data.chunks_indexed
        ? `${file.name} · ${data.chunks_indexed} chunks indexed for RAG`
        : `${file.name} ingested`;
      toast.success(msg);
      load();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const setRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}/role?role=${role}`);
      toast.success(`Role updated → ${role}`);
      load();
    } catch (_e) { toast.error("Failed to update role"); }
  };

  const mineFile = async (f) => {
    setMining(f.id);
    try {
      const { data } = await api.post("/mining/discover", { file_id: f.id, name: `Mined · ${f.original_filename}` });
      toast.success(`Discovered '${data.name}' — ${data.cases} cases · ${data.variants} variants`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mining failed");
    } finally { setMining(null); }
  };

  const saveSN = async (patch) => {
    try {
      await api.post("/connectors/servicenow/config", { ...(sn?.config || {}), ...patch });
      toast.success("ServiceNow config saved");
      load();
    } catch (_e) { toast.error("Failed to save"); }
  };

  const syncSN = async () => {
    setSnSyncing(true);
    try {
      const { data } = await api.post("/connectors/servicenow/sync");
      toast.success(`Synced ${data.summary.total} incidents · +${data.new_anomalies} new anomalies`);
      load();
    } catch (_e) { toast.error("Sync failed"); }
    finally { setSnSyncing(false); }
  };

  const addRecipient = async () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    if ((digest?.recipients || []).includes(email)) return toast.error("Already added");
    try {
      await api.post("/digest/settings", {
        recipients: [...(digest?.recipients || []), email],
        enabled: digest?.enabled ?? true,
        subject: digest?.subject,
      });
      setNewRecipient("");
      toast.success(`Added ${email}`);
      load();
    } catch (_e) { toast.error("Failed to add recipient"); }
  };

  const removeRecipient = async (email) => {
    try {
      await api.post("/digest/settings", {
        recipients: (digest?.recipients || []).filter((r) => r !== email),
        enabled: digest?.enabled ?? true,
        subject: digest?.subject,
      });
      toast.success(`Removed ${email}`);
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  const toggleDigest = async (enabled) => {
    try {
      await api.post("/digest/settings", {
        recipients: digest?.recipients || [],
        enabled,
        subject: digest?.subject,
      });
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  const sendTest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/digest/send-test");
      toast.success(`Sent to ${data.recipients.length} recipient${data.recipients.length > 1 ? "s" : ""} · id ${data.email_id.slice(0, 8)}…`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setDigestBusy(false); }
  };

  const preview = async () => {
    try {
      const { data } = await api.get("/digest/preview");
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch (_e) { toast.error("Preview failed"); }
  };

  const saveSender = async () => {
    try {
      await api.post("/digest/settings", {
        recipients: digest?.recipients || [],
        enabled: digest?.enabled ?? true,
        subject: digest?.subject,
        sender: senderDraft.trim(),
      });
      toast.success(senderDraft.trim() ? "Sender updated" : "Sender reset to default");
      load();
    } catch (_e) { toast.error("Failed to save sender"); }
  };

  const runReaper = async () => {
    setReaping(true);
    try {
      const { data } = await api.post("/admin/reaper/run");
      toast.success(`Reaper hard-deleted ${data.reaped} file${data.reaped !== 1 ? "s" : ""}`);
      load();
    } catch (_e) { toast.error("Reaper failed"); }
    finally { setReaping(false); }
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${api.defaults.baseURL}/export/snapshot?include_files=${includeFiles}`, {
        method: "POST",
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("itsme_token") || ""}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `itsme-snapshot-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Snapshot downloaded (${(blob.size / 1024).toFixed(1)} KB)`);
    } catch (_e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="data-label">Governance</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Administration & Settings</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Ingest sources, mine processes, manage connectors, teams, and guardrails.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Ingestion */}
        <div className="itsme-card p-5">
          <div className="flex items-center gap-2">
            <UploadSimple size={18} className="text-[#00E5FF]" />
            <div className="font-heading text-lg font-semibold">Ingestion</div>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            SOPs (PDF/DOCX/TXT) are extracted and indexed for RAG. Event logs (CSV/XES/LOG/LST) can be mined into processes.
          </p>

          <label className="mt-4 flex flex-col items-center justify-center gap-2 border border-dashed border-[#272A35] rounded-md p-8 cursor-pointer hover:bg-[#13151A] transition-colors"
                 data-testid="upload-dropzone">
            <FileArrowUp size={28} className="text-slate-500" />
            <div className="text-sm text-slate-300">{uploading ? "Uploading & extracting…" : "Drop file or click to select"}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">PDF · DOCX · TXT · CSV · XES · LOG · LST · XLSX · JSON</div>
            <input ref={fileInputRef} type="file" hidden onChange={onUpload}
                   accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.xes,.log,.lst,.png,.jpg,.jpeg,.json,.xml,.txt,.md" />
          </label>

          <div className="mt-4">
            <div className="data-label">Ingested Files</div>
            <div className="mt-2 divide-y divide-[#272A35] max-h-72 overflow-auto">
              {files.length === 0 && <div className="text-xs text-slate-500 py-2">No files yet.</div>}
              {files.map((f) => (
                <div key={f.id} data-testid={`file-${f.id}`} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{f.original_filename}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-2">
                      <span>{f.kind}</span>
                      <span>·</span>
                      <span>{(f.size / 1024).toFixed(1)} KB</span>
                      {f.chunks_indexed ? (
                        <><span>·</span><span className="text-[#00E5FF]">{f.chunks_indexed} RAG chunks</span></>
                      ) : null}
                    </div>
                  </div>
                  {f.kind === "event_log" && (
                    <Button size="sm" data-testid={`mine-${f.id}`}
                            disabled={mining === f.id}
                            onClick={() => mineFile(f)}
                            className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 hover:bg-[#00E5FF]/20 h-7">
                      <Graph size={12} className="mr-1" /> {mining === f.id ? "Mining…" : "Mine process"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ServiceNow Connector */}
        <div className="itsme-card p-5" data-testid="servicenow-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LinkSimple size={18} className="text-[#00E5FF]" />
              <div className="font-heading text-lg font-semibold">ServiceNow Connector</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border"
                 style={{
                   color: sn?.config?.mode === "live" ? "#00E676" : "#FFB000",
                   borderColor: sn?.config?.mode === "live" ? "rgba(0,230,118,0.3)" : "rgba(255,176,0,0.3)"
                 }}>
              {sn?.config?.mode || "demo"} mode
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Sync incidents from ServiceNow. Leave credentials blank to run in demo mode with synthetic incidents.
          </p>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="data-label">Instance URL</Label>
              <Input data-testid="sn-instance" placeholder="https://acme.service-now.com"
                     value={sn?.config?.instance || ""}
                     onChange={(e) => setSn({ ...sn, config: { ...(sn?.config || {}), instance: e.target.value } })}
                     className="mt-1 bg-[#0F1117] border-[#272A35]" />
            </div>
            <div>
              <Label className="data-label">Username</Label>
              <Input data-testid="sn-username" placeholder="itsme.svc"
                     value={sn?.config?.username || ""}
                     onChange={(e) => setSn({ ...sn, config: { ...(sn?.config || {}), username: e.target.value } })}
                     className="mt-1 bg-[#0F1117] border-[#272A35]" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-200">Enabled</Label>
              <Switch data-testid="sn-enabled" checked={!!sn?.config?.enabled}
                      onCheckedChange={(v) => saveSN({ enabled: v })} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => saveSN({})}
                      className="border-[#272A35] text-slate-300" data-testid="sn-save">Save</Button>
              <Button size="sm" data-testid="sn-sync" onClick={syncSN} disabled={snSyncing}
                      className="bg-[#0055FF] hover:bg-[#0044cc]">
                <ArrowsClockwise size={14} className="mr-1" /> {snSyncing ? "Syncing…" : "Sync now"}
              </Button>
            </div>
          </div>

          {sn?.summary?.total > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3">
              <MiniStat label="Total" value={sn.summary.total} />
              <MiniStat label="Open" value={sn.summary.open} color="#FFB000" />
              <MiniStat label="Critical open" value={sn.summary.critical_open} color="#FF3B30" />
            </div>
          )}

          {sn?.recent_incidents?.length > 0 && (
            <div className="mt-4">
              <div className="data-label">Latest incidents</div>
              <div className="mt-2 max-h-40 overflow-auto divide-y divide-[#272A35]">
                {sn.recent_incidents.slice(0, 6).map((i) => (
                  <div key={i.sys_id} className="py-2 text-xs flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-200 truncate">{i.short_description}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{i.number} · {i.category} · {i.assignment_group}</div>
                    </div>
                    <div className="text-[10px] text-slate-400">{i.priority}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Weekly Digest */}
      {user?.role === "admin" && (
        <div className="itsme-card p-5" data-testid="digest-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Envelope size={18} className="text-[#00E5FF]" />
              <div className="font-heading text-lg font-semibold">Weekly Digest</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Auto-send Monday 09:00 UTC</span>
              <Switch data-testid="digest-enabled" checked={!!digest?.enabled}
                      onCheckedChange={toggleDigest} />
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Every Monday morning, leadership gets an HTML digest with KPIs, new anomalies, top recommendations, and newly mined processes from the last 7 days.
          </p>
          <div className="text-[11px] text-slate-500 mt-1 font-mono">
            Sender: {digest?.sender || "onboarding@resend.dev"}
            {digest?.is_sandbox && (
              <span className="text-[#FFB000] ml-2">· Sandbox mode: emails only deliver to the Resend account owner.</span>
            )}
          </div>

          {/* Sender override + domain verify help */}
          <div className="mt-3 itsme-card p-3 border-dashed" data-testid="sender-override">
            <div className="flex items-center justify-between">
              <div>
                <div className="data-label">From address (override)</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  After you verify a domain on Resend, paste the address here (e.g. <span className="font-mono text-slate-300">digest@yourdomain.com</span>) to deliver to the full recipient list.
                </div>
              </div>
              <button
                onClick={() => setShowDomainHelp((v) => !v)}
                data-testid="domain-help-toggle"
                className="text-[11px] text-[#00E5FF] hover:underline shrink-0 ml-4"
              >
                {showDomainHelp ? "Hide" : "How to verify a domain"}
              </button>
            </div>
            {showDomainHelp && (
              <ol className="mt-3 text-xs text-slate-300 list-decimal list-inside space-y-1.5 leading-relaxed" data-testid="domain-help-body">
                <li>Open <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#00E5FF] underline">resend.com/domains</a> and click <span className="font-mono">Add Domain</span>.</li>
                <li>Add the domain you own (e.g. <span className="font-mono">yourcompany.com</span>).</li>
                <li>Copy the three DNS records Resend shows (SPF, DKIM, MX) into your DNS provider (Cloudflare, Route 53, Namecheap…).</li>
                <li>Wait for verification to turn green (usually 5–30 min).</li>
                <li>Paste an address on that domain below and click <span className="font-mono">Save sender</span>.</li>
              </ol>
            )}
            <div className="mt-3 flex gap-2">
              <Input
                data-testid="sender-input"
                placeholder={`Leave blank to use ${digest?.sender_default || "onboarding@resend.dev"}`}
                value={senderDraft}
                onChange={(e) => setSenderDraft(e.target.value)}
                className="flex-1 bg-[#0F1117] border-[#272A35] font-mono text-sm"
              />
              <Button data-testid="sender-save" size="sm" onClick={saveSender}
                      className="bg-[#0055FF] hover:bg-[#0044cc]">Save sender</Button>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Input data-testid="digest-recipient-input" placeholder="leader@company.com"
                   value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                   className="flex-1 bg-[#0F1117] border-[#272A35]" />
            <Button data-testid="digest-add" onClick={addRecipient}
                    className="bg-[#0055FF] hover:bg-[#0044cc]">Add recipient</Button>
          </div>

          {digest?.recipients?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {digest.recipients.map((r) => (
                <div key={r} data-testid={`recipient-${r}`}
                     className="flex items-center gap-2 bg-[#13151A] border border-[#272A35] rounded-md px-3 py-1.5 text-sm">
                  <span className="text-slate-200">{r}</span>
                  <button onClick={() => removeRecipient(r)}
                          data-testid={`remove-${r}`}
                          className="text-slate-500 hover:text-[#FF3B30]"
                          title="Remove">
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" data-testid="digest-preview"
                    onClick={preview}
                    className="border-[#272A35] text-slate-300">
              <Eye size={14} className="mr-1" /> Preview
            </Button>
            <Button size="sm" data-testid="digest-send-test"
                    onClick={sendTest} disabled={digestBusy || !digest?.recipients?.length}
                    className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 hover:bg-[#00E5FF]/20">
              <Envelope size={14} className="mr-1" /> {digestBusy ? "Sending…" : "Send test digest now"}
            </Button>

            {digest?.last_sent_at && (
              <div className="text-[11px] text-slate-500 ml-auto">
                Last sent: {new Date(digest.last_sent_at).toLocaleString()}
                {digest.last_result?.trigger && (
                  <span className="ml-1 text-slate-400">· {digest.last_result.trigger}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users */}
      {user?.role === "admin" && (
        <div className="itsme-card p-5">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#00E5FF]" />
            <div className="font-heading text-lg font-semibold">Users & Roles</div>
          </div>
          <div className="mt-4 divide-y divide-[#272A35]">
            <div className="grid grid-cols-[1fr,1fr,160px,120px] gap-3 pb-2 data-label">
              <div>Name</div><div>Email</div><div>Role</div><div>Since</div>
            </div>
            {users.map((u) => (
              <div key={u.id} data-testid={`user-${u.id}`}
                   className="grid grid-cols-[1fr,1fr,160px,120px] gap-3 py-3 items-center text-sm">
                <div>{u.name}</div>
                <div className="text-slate-400 truncate">{u.email}</div>
                <Select value={u.role} onValueChange={(r) => setRole(u.id, r)}>
                  <SelectTrigger data-testid={`role-${u.id}`} className="bg-[#0F1117] border-[#272A35] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#13151A] border-[#272A35] text-slate-100">
                    <SelectItem value="admin">admin</SelectItem>
                    <SelectItem value="analyst">analyst</SelectItem>
                    <SelectItem value="viewer">viewer</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backup Snapshot Export */}
      {user?.role === "admin" && exportStatus && (
        <div className="itsme-card p-5" data-testid="export-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Archive size={18} className="text-[#00E5FF]" />
              <div className="font-heading text-lg font-semibold">Backup Snapshot</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Compliance archive · zip
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Download a full snapshot of every process, anomaly, recommendation, simulation, report, advisory, and file — in one signed zip suitable for compliance archives.
          </p>

          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            {Object.entries(exportStatus.counts).map(([k, v]) => (
              <MiniStat key={k} label={k.replace("_", " ")} value={v} />
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <Switch data-testid="export-include-files"
                      checked={includeFiles}
                      onCheckedChange={setIncludeFiles} />
              Include file binaries
              <span className="text-[10px] text-slate-500 ml-1">
                ({(exportStatus.estimated_files_bytes / 1024).toFixed(1)} KB)
              </span>
            </label>
            <div className="flex-1" />
            <Button size="sm" data-testid="export-run"
                    onClick={runExport} disabled={exporting}
                    className="bg-[#0055FF] hover:bg-[#0044cc]">
              <DownloadSimple size={14} className="mr-1" /> {exporting ? "Building snapshot…" : "Download snapshot"}
            </Button>
          </div>
        </div>
      )}

      {/* Storage retention reaper */}
      {user?.role === "admin" && reaper && (
        <div className="itsme-card p-5" data-testid="reaper-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Broom size={18} className="text-[#00E5FF]" />
              <div className="font-heading text-lg font-semibold">Storage Retention</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Nightly · {reaper.retention_days}-day window
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Soft-deleted files are hard-deleted from object storage after {reaper.retention_days} days to prevent bloat. Runs automatically once per day.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <MiniStat label="Retention window" value={`${reaper.retention_days}d`} />
            <MiniStat label="Soft-deleted" value={reaper.soft_deleted} color="#FFB000" />
            <MiniStat label="Reapable now" value={reaper.reapable_now} color={reaper.reapable_now > 0 ? "#FF3B30" : "#F3F4F6"} />
          </div>
          <div className="mt-4 flex justify-end">
            <Button size="sm" data-testid="reaper-run"
                    onClick={runReaper} disabled={reaping}
                    className="bg-[#0055FF] hover:bg-[#0044cc]">
              <Broom size={14} className="mr-1" /> {reaping ? "Reaping…" : "Run reaper now"}
            </Button>
          </div>
        </div>
      )}

      {/* Connector directory */}
      <div className="itsme-card p-5">
        <div className="flex items-center gap-2">
          <Plugs size={18} className="text-[#00E5FF]" />
          <div className="font-heading text-lg font-semibold">Connector Directory</div>
        </div>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[
            { name: "ServiceNow", status: "Live", color: "#00E676" },
            { name: "Dynatrace", status: "Adapter", color: "#9CA3AF" },
            { name: "Slack", status: "Beta", color: "#FFB000" },
            { name: "SAP", status: "Adapter", color: "#9CA3AF" },
            { name: "Jira", status: "Adapter", color: "#9CA3AF" },
            { name: "PagerDuty", status: "Adapter", color: "#9CA3AF" },
          ].map((c) => (
            <div key={c.name} className="itsme-card p-3 itsme-card-hover">
              <div className="text-sm font-medium">{c.name}</div>
              <div className="text-[10px] uppercase tracking-widest mt-2" style={{ color: c.color }}>{c.status}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={() => setPreviewOpen(false)} data-testid="digest-preview-modal">
          <div className="bg-[#0B0D12] border border-[#272A35] rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#272A35] flex items-center justify-between">
              <div className="font-heading font-semibold flex items-center gap-2">
                <Envelope size={18} className="text-[#00E5FF]" /> Digest Preview
              </div>
              <button onClick={() => setPreviewOpen(false)}
                      className="text-slate-400 hover:text-white text-sm" data-testid="digest-preview-close">Close ✕</button>
            </div>
            <iframe title="digest-preview" srcDoc={previewHtml}
                    className="flex-1 w-full bg-[#090A0E] border-0" />
          </div>
        </div>
      )}
    </div>
  );
}

const MiniStat = ({ label, value, color = "#F3F4F6" }) => (
  <div className="itsme-card p-3">
    <div className="data-label">{label}</div>
    <div className="font-heading text-2xl font-bold mt-1" style={{ color }}>{value}</div>
  </div>
);
